import { AnimatePresence, motion } from 'framer-motion';
import React from 'react';

interface VisualEffectsSectionProps {
  glowEnabled: boolean;
  setGlowEnabled: (v: boolean) => void;
  glowIntensity: number;
  setGlowIntensity: (v: number) => void;
  focusModeEnabled: boolean;
  setFocusModeEnabled: (v: boolean) => void;
  focusLineCount: number;
  setFocusLineCount: (v: number) => void;
}

export const VisualEffectsSection: React.FC<VisualEffectsSectionProps> = React.memo(
  ({
    glowEnabled,
    setGlowEnabled,
    glowIntensity,
    setGlowIntensity,
    focusModeEnabled,
    setFocusModeEnabled,
    focusLineCount,
    setFocusLineCount,
  }) => {
    return (
      <>
        {/* フォーカス・リーディングモード */}
        <div className="space-y-3.5 p-3 rounded-md border border-neutral-500/10">
          <div className="flex items-center justify-between">
            <div className="space-y-0.5">
              <div className="text-xs font-bold">フォーカス・リーディング</div>
              <div className="text-[10px] opacity-40">注視行以外をフェードアウトして没入</div>
            </div>
            <label className="relative inline-flex items-center cursor-pointer select-none">
              <input
                type="checkbox"
                checked={focusModeEnabled}
                onChange={(e) => setFocusModeEnabled(e.target.checked)}
                className="sr-only peer"
              />
              <div className="w-9 h-5 bg-neutral-300 dark:bg-neutral-700 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-neutral-850 dark:peer-checked:bg-neutral-100" />
            </label>
          </div>

          <AnimatePresence initial={false}>
            {focusModeEnabled && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                className="space-y-1 pt-1 overflow-hidden"
              >
                <div className="flex justify-between text-[11px] font-bold">
                  <span>フォーカス対象行数</span>
                  <span className="font-mono">{focusLineCount} 行</span>
                </div>
                <input
                  type="range"
                  min="1"
                  max="9"
                  step="2"
                  value={focusLineCount}
                  onChange={(e) => setFocusLineCount(Number(e.target.value))}
                  className="w-full accent-neutral-850 dark:accent-neutral-200 h-11"
                />
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* アンビエント・グロウ */}
        <div className="space-y-3.5 p-3 rounded-md border border-neutral-500/10">
          <div className="flex items-center justify-between">
            <div className="space-y-0.5">
              <div className="text-xs font-bold">アンビエント・グロウ</div>
              <div className="text-[10px] opacity-40">背景と同調した柔らかな光を画面外縁に投影</div>
            </div>
            <label className="relative inline-flex items-center cursor-pointer select-none">
              <input
                type="checkbox"
                checked={glowEnabled}
                onChange={(e) => setGlowEnabled(e.target.checked)}
                className="sr-only peer"
              />
              <div className="w-9 h-5 bg-neutral-300 dark:bg-neutral-700 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-neutral-850 dark:peer-checked:bg-neutral-100" />
            </label>
          </div>

          <AnimatePresence initial={false}>
            {glowEnabled && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                className="space-y-1 pt-1 border-t border-current/5 mt-2 overflow-hidden"
              >
                <div className="flex justify-between text-[11px] font-bold">
                  <span>発光強度</span>
                  <span className="font-mono">{Math.round(glowIntensity * 100)}%</span>
                </div>
                <input
                  type="range"
                  min="0.1"
                  max="1"
                  step="0.05"
                  value={glowIntensity}
                  onChange={(e) => setGlowIntensity(Number(e.target.value))}
                  className="w-full accent-neutral-850 dark:accent-neutral-200 h-11"
                />
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </>
    );
  },
);

VisualEffectsSection.displayName = 'VisualEffectsSection';
